import type { MenuChild, MenuList } from '../../interface/layout/menu.interface';
import type { FC } from 'react';

import './index.less';

import { Drawer, Layout, theme as antTheme } from 'antd';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router';

import { setUserItem } from '../../stores/user.store';
import { getFirstPathCode } from '../../utils/getFirstPathCode';
import { getGlobalState } from '../../utils/getGloabal';

import { useGuide } from '../guide/useGuide';
import HeaderComponent from './header';
import MenuComponent from './menu';
import TagsView from './tagView';
import { result } from '../../../Utils/sidebar';
import { Navigate } from 'react-router-dom';
import { useUnreadCount } from '../../../Hooks/TT_ELD/Admin';
import { isWhiteLabelAdmin, WHITE_LABEL_HIDDEN_CODES } from '../../../Utils/whiteLabel';
import { EProjectNames, useProjectData } from '../../../Hooks/useProjectData';

const { Sider, Content, Header } = Layout;
const WIDTH = 992;

const LayoutPage: FC = () => {
  const { projectData } = useProjectData();
  const isAuthenticated = localStorage.getItem('token') as string;
  const [authorized, setAuthorized] = useState<string | null>(isAuthenticated);

  const location = useLocation();
  const [openKey, setOpenkey] = useState<string>();
  const [selectedKey, setSelectedKey] = useState<string>(location.pathname);
  const [menuList, setMenuList] = useState<MenuList>([]);
  const { device, collapsed, newUser } = useSelector((state: any) => state.user);
  const token = antTheme.useToken();

  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setEnabled(true);
  }, []);

  const { data: unreadCount } = useUnreadCount(enabled);

  const getUserRole = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.role;
    } catch {
      return null;
    }
  };

  const filterMenuForWhiteLabel = (menus: MenuList): MenuList => {
    if (!isWhiteLabelAdmin()) return menus;
    const hiddenCodes =
      projectData?.projectName === EProjectNames.RTE
        ? WHITE_LABEL_HIDDEN_CODES.filter((code) => code !== 'sales')
        : WHITE_LABEL_HIDDEN_CODES;

    return menus
      .filter((item) => !hiddenCodes.includes(item.code))
      .map((item) => {
        if (item.children) {
          return {
            ...item,
            children: filterMenuForWhiteLabel(item.children),
          };
        }
        return item;
      })
      .filter((item) => {
        if (item.children && item.children.length === 0) return false;
        return true;
      });
  };

  const filterMenuByRole = (menus: MenuList, role: string): MenuList => {
    const isRteProject = projectData?.projectName === EProjectNames.RTE;

    return menus
      .filter((item) => {
        if (isRteProject && item.code === 'sales') {
          const rteSalesRoles = ['sales', 'manager', 'support', 'support_manager', 'developer'];
          return rteSalesRoles.includes(role);
        }

        if (isRteProject && item.code === 'unpaid-companies') {
          const rteUnpaidRoles = ['sales', 'manager', 'support', 'support_manager', 'developer'];
          return rteUnpaidRoles.includes(role);
        }

        if (item.role && !item.role.includes(role)) return false;
        return true;
      })
      .map((item) => {
        if (item.children) {
          const children = filterMenuByRole(item.children, role);
          return { ...item, children };
        }
        return item;
      })
      .filter((item) => {
        if (item.children && item.children.length === 0) return false;
        return true;
      });
  };

  const isMobile = device === 'MOBILE';
  const dispatch = useDispatch();
  const { driverStart } = useGuide();
  useEffect(() => {
    const code = getFirstPathCode(location.pathname);

    setOpenkey(code);
    setSelectedKey(location.pathname);
  }, [location.pathname]);

  const toggle = () => {
    dispatch(
      setUserItem({
        collapsed: !collapsed,
      }),
    );
  };

  const initMenuListAll = (menu: MenuList) => {
    const MenuListAll: MenuChild[] = [];

    menu?.forEach((m) => {
      if (!m?.children?.length) {
        MenuListAll.push(m);
      } else {
        m?.children?.forEach((mu) => {
          MenuListAll.push(mu);
        });
      }
    });

    return MenuListAll;
  };
  const fetchMenuList = useCallback(async () => {
    const role = getUserRole();
    const isWhiteLabel = isWhiteLabelAdmin();

    if (result) {
      let filteredList: MenuList;

      if (isWhiteLabel) {
        filteredList = filterMenuForWhiteLabel(result);
      } else {
        filteredList = role ? filterMenuByRole(result, role) : result;
      }

      const badgeKeyMap: Record<string, string> = {
        contact: 'unreadLeadsCount',
        account: 'unreadRegisterUsersCount',
        contracts: 'unreadContractsCount',
        'dashcam-request': 'unreadDashcamRequestsCount',
        fmcsa: 'unreadFmcsaCount',
        'linked-companies': 'unreadCompanyContractRequestCount',
      };

      const getSalesBadge = () => {
        if (!unreadCount) return undefined;

        const contractsCount = unreadCount.unreadContractsCount || 0;
        const linkedCompanies = unreadCount.unreadCompanyContractRequestCount || 0;
        const dashcamCount = unreadCount.unreadDashcamRequestsCount || 0;
        const sum = contractsCount + dashcamCount + linkedCompanies;
        return sum > 0 ? sum : undefined;
      };

      const menuListWithBadge = filteredList.map((menu) => {
        let badgeCount: number | undefined;

        if (menu.code === 'sales') {
          badgeCount = getSalesBadge();
        } else {
          const badgeKey = badgeKeyMap[menu.code || ''];
          badgeCount = badgeKey && unreadCount ? unreadCount[badgeKey] : undefined;
        }

        const childrenWithBadge = menu.children
          ? menu.children.map((child) => {
              const childBadgeKey = badgeKeyMap[child.code || ''];
              const childBadgeCount =
                childBadgeKey && unreadCount ? unreadCount[childBadgeKey] : undefined;

              const isRteUnpaidMenuItem =
                projectData?.projectName === EProjectNames.RTE &&
                menu.code === 'sales' &&
                child.code === 'unpaid-companies';

              return {
                ...child,
                ...(isRteUnpaidMenuItem
                  ? {
                      label: {
                        ...child.label,
                        en_US: 'Unpaid Invoices',
                      },
                    }
                  : {}),
                badge: childBadgeCount && childBadgeCount > 0 ? childBadgeCount : undefined,
              };
            })
          : undefined;

        return {
          ...menu,
          badge: badgeCount,
          children: childrenWithBadge || menu.children,
        };
      });

      setMenuList(menuListWithBadge);

      dispatch(
        setUserItem({
          menuList: initMenuListAll(menuListWithBadge),
        }),
      );
    }
  }, [dispatch, unreadCount, projectData?.projectName]);

  // const fetchMenuList = useCallback(async () => {
  //   if (result) {
  //     setMenuList(result);
  //     dispatch(
  //       setUserItem({
  //         menuList: initMenuListAll(result),
  //       }),
  //     );
  //   }
  // }, [dispatch]);

  useEffect(() => {
    fetchMenuList();
  }, [fetchMenuList]);

  useEffect(() => {
    window.onresize = () => {
      const { device } = getGlobalState();
      const rect = document.body.getBoundingClientRect();
      const needCollapse = rect.width < WIDTH;

      dispatch(
        setUserItem({
          device,
          collapsed: needCollapse,
        }),
      );
    };
  }, [dispatch]);

  useEffect(() => {
    newUser && driverStart();
  }, [newUser]);

  return (
    <>
      {!authorized && location.pathname !== '/login' && (
        <Navigate
          to={{
            pathname: '/login',
          }}
        />
      )}
      {authorized && location.pathname === '/login' && (
        <Navigate
          to={{
            pathname: '/',
          }}
        />
      )}
      {authorized ? (
        <Layout className="layout-page">
          <HeaderComponent collapsed={collapsed} toggle={toggle} />
          <Layout>
            {!isMobile ? (
              <Sider
                className="layout-page-sider"
                trigger={null}
                collapsible
                style={{ backgroundColor: token.token.colorBgContainer }}
                collapsedWidth={isMobile ? 0 : 80}
                collapsed={collapsed}
                breakpoint="md"
                width="225px"
              >
                <MenuComponent
                  menuList={menuList}
                  openKey={openKey}
                  onChangeOpenKey={(k) => setOpenkey(k)}
                  selectedKey={selectedKey}
                  onChangeSelectedKey={(k) => setSelectedKey(k)}
                />
              </Sider>
            ) : (
              <Drawer
                width="200"
                placement="left"
                bodyStyle={{ padding: 0, height: '100%' }}
                closable={false}
                onClose={toggle}
                open={!collapsed}
              >
                <MenuComponent
                  menuList={menuList}
                  openKey={openKey}
                  onChangeOpenKey={(k) => setOpenkey(k)}
                  selectedKey={selectedKey}
                  onChangeSelectedKey={(k) => setSelectedKey(k)}
                />
              </Drawer>
            )}
            <Content className="layout-page-content">
              <TagsView />
              <div className="MainContent">
                <Suspense fallback={null}>
                  <Outlet />
                </Suspense>
              </div>
            </Content>
          </Layout>
        </Layout>
      ) : (
        <></>
      )}
    </>
  );
};

export default LayoutPage;
